import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowLeft, BarChart3, Building2, CheckCircle2, CreditCard, LoaderCircle, Mail, RefreshCw, Search, ShieldAlert, ShieldCheck, Trash2, TrendingUp, Users, Wallet, Webhook, XCircle } from "lucide-react";
import { api } from "../lib/api";

type AdminTab = "overview" | "ledger" | "spenders" | "failures" | "users" | "workspaces" | "events" | "admins";

export type Period = { days?: number; from?: string; to?: string } | null;

export function periodQuery(period: Period): string {
  if (!period) return "";
  if (period.days) return `days=${period.days}`;
  const parts: string[] = [];
  if (period.from) parts.push(`from=${period.from}`);
  if (period.to) parts.push(`to=${period.to}`);
  return parts.join("&");
}

function withPeriod(path: string, period: Period, extra = ""): string {
  const query = [periodQuery(period), extra].filter(Boolean).join("&");
  return query ? `${path}?${query}` : path;
}

function PeriodBar({ period, onChange, range }: { period: Period; onChange: (next: Period) => void; range: { from: string; to: string } | null }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const activeDays = period?.days || (!period ? 30 : undefined);
  return <div className="admin-period" role="group" aria-label="Reporting period">
    {[7, 30, 90].map((days) => <button key={days} type="button" className={activeDays === days ? "is-active" : ""} onClick={() => { setFrom(""); setTo(""); onChange({ days }); }}>{days}d</button>)}
    <span className="admin-period__custom">
      <input type="date" value={from || range?.from || ""} max={to || range?.to || undefined} onChange={(event) => setFrom(event.target.value)} aria-label="From date" />
      <span className="admin-muted">to</span>
      <input type="date" value={to || range?.to || ""} min={from || range?.from || undefined} onChange={(event) => setTo(event.target.value)} aria-label="To date" />
      <button type="button" className="admin-period__apply" disabled={!from && !to} onClick={() => onChange({ from: from || range?.from, to: to || range?.to })}>Apply</button>
    </span>
    {period && !period.days && <span className="admin-status is-active">custom range</span>}
  </div>;
}

type AdminWorkspace = { id: string; name: string; whop_account_id: string; owner_email: string; members: number; created_at: string };
type AdminTotals = { workspaces: number; clients: number; teammates: number; pending_invites: number; new_this_week: number };
type AdminWorkspacesResponse = { totals: AdminTotals; workspaces: AdminWorkspace[] };
type AdminMember = { id: string; email: string; role: "admin" | "member"; status: "invited" | "active"; permissions: string[]; created_at: string };
type AdminWorkspaceDetail = {
  workspace: { id: string; name: string; whop_account_id: string; owner_email: string; created_at: string };
  members: AdminMember[];
  setup: { billingReady: boolean; identityReady: boolean } | null;
  campaigns: { total: number; active: number } | null;
};
type AdminUser = { id: string; email: string; created_at: string | null; last_sign_in_at: string | null; account_count: number; account_names: string[] };
type MetricsResponse = {
  today: string;
  from: string;
  to: string;
  last_synced_date: string | null;
  last_refreshed_at: string | null;
  daily: Array<{ date: string; spend: number }>;
  month: { spend: number; rate: number; profit: number };
  previous_month: { spend: number; rate: number; profit: number };
  new_logos_daily: Array<{ date: string; count: number }>;
  new_logos: { count: number; owners: Array<{ name: string; email: string; created_at: string }> };
  churned_logos: { count: number; owners: string[] };
  nrr_weeks: Array<{ week_start: string; prior_spend: number; retained_spend: number; nrr: number | null }>;
  top_spenders: Array<{
    whop_account_id: string;
    name: string;
    owner_email: string;
    first_1k_spend_date: string | null;
    today_spend: number;
    dod_change_pct: number | null;
    l3_spend: number;
    l3_change_pct: number | null;
    l7_spend: number;
    wow_change_pct: number | null;
    l30_spend: number;
    l30_change_pct: number | null;
    spend_total: number;
    last_spend_date: string | null;
    daily: number[];
    range_spend: number;
  }>;
};
type FailuresResponse = {
  from: string;
  to: string;
  history: Array<{ id: string; campaign_id: string; account_id: string; account_name: string; owner_email: string; title: string; first_seen_at: string; resolved_at: string | null }>;
  failures: Array<{ campaign_id: string; title: string; status: string; delivery_status: string; updated_at: string | null; account_id: string; account_name: string; owner_email: string }>;
  launch_failures: Array<{ id: string; campaign_id: string; account_id: string; account_name: string; owner_email: string; reason: string; created_at: string }>;
  webhook_events: Array<{ event_id: string; type: string; company_id: string | null; verified: boolean; received_at: string }>;
};

function formatDate(value: string | null) {
  if (!value) return "—";
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return "—";
  return new Date(parsed).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function money(value: number) {
  return value.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: value >= 1000 ? 0 : 2 });
}

function niceCeiling(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (value <= step * magnitude) return step * magnitude;
  }
  return 10 * magnitude;
}

function compactNumber(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value % 1_000_000 === 0 ? 0 : 1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value % 1_000 === 0 ? 0 : 1)}k`;
  return `${Math.round(value * 100) / 100}`;
}

// Full-size bar chart with labeled, gridded axes. Pure SVG, no library.
function Bars({ data, ariaLabel, format, unit }: { data: Array<{ label: string; value: number }>; ariaLabel: string; format?: (value: number) => string; unit?: string }) {
  const plotWidth = 860;
  const plotHeight = 260;
  const left = 74;
  const bottom = 52;
  const top = 12;
  const max = niceCeiling(Math.max(...data.map((entry) => entry.value), 1));
  const barWidth = plotWidth / Math.max(data.length, 1);
  const describe = format || ((value: number) => String(value));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((fraction) => max * fraction);
  const rotateLabels = data.length > 12;
  return <svg className="admin-chart" viewBox={`0 0 ${left + plotWidth + 16} ${top + plotHeight + bottom}`} role="img" aria-label={ariaLabel}>
    {ticks.map((tick) => {
      const y = top + plotHeight - (tick / max) * plotHeight;
      return <g key={tick}>
        <line x1={left} x2={left + plotWidth} y1={y} y2={y} className="admin-chart__grid" />
        <text x={left - 8} y={y + 4} className="admin-chart__tick" textAnchor="end">{unit === "$" ? `$${compactNumber(tick)}` : `${compactNumber(tick)}${unit || ""}`}</text>
      </g>;
    })}
    <line x1={left} x2={left} y1={top} y2={top + plotHeight} className="admin-chart__axis" />
    <line x1={left} x2={left + plotWidth} y1={top + plotHeight} y2={top + plotHeight} className="admin-chart__axis" />
    {data.map((entry, index) => {
      const barHeight = entry.value > 0 ? Math.max(3, (entry.value / max) * plotHeight) : 0;
      const x = left + index * barWidth + 2;
      const width = Math.max(barWidth - 4, 2);
      const barTop = top + plotHeight - barHeight;
      return <g key={entry.label}>
        {entry.value > 0 && <rect x={x} y={barTop} width={width} height={barHeight} rx={3} className="admin-chart__bar">
          <title>{`${entry.label}: ${describe(entry.value)}`}</title>
        </rect>}
        {entry.value > 0 && <text x={x + width / 2} y={Math.max(barTop - 6, top + 10)} className="admin-chart__value" textAnchor="middle">{unit === "$" ? `$${compactNumber(entry.value)}` : `${compactNumber(entry.value)}${unit || ""}`}</text>}
        {rotateLabels
          ? <text x={x + width / 2} y={top + plotHeight + 12} className="admin-chart__label" textAnchor="end" transform={`rotate(-45 ${x + width / 2} ${top + plotHeight + 12})`}>{entry.label.length > 5 ? entry.label.slice(5) : entry.label}</text>
          : <text x={x + width / 2} y={top + plotHeight + 18} className="admin-chart__label" textAnchor="middle">{entry.label.length > 5 ? entry.label.slice(5) : entry.label}</text>}
      </g>;
    })}
  </svg>;
}

function Sparkline({ values, tall }: { values: number[]; tall?: boolean }) {
  const max = Math.max(...values, 1);
  const width = tall ? 320 : 260;
  const height = tall ? 130 : 28;
  const barWidth = width / Math.max(values.length, 1);
  return <svg className={`admin-spark ${tall ? "admin-spark--tall" : ""}`} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
    <line x1={0} x2={width} y1={height - 0.75} y2={height - 0.75} className="admin-chart__baseline" />
    {values.map((value, index) => {
      if (value <= 0) return null;
      const barHeight = Math.max(3, (value / max) * height);
      return <rect key={index} x={index * barWidth} y={height - barHeight} width={Math.max(barWidth - 1.5, 1.5)} height={barHeight} rx={1.5} className="admin-chart__bar" />;
    })}
  </svg>;
}

// Pure: the series ends on the newest date in the data, so rendering
// needs no clock access.
function dailyCounts(dates: Array<string | null | undefined>, days: number): Array<{ label: string; value: number }> {
  const counts = new Map<string, number>();
  let latest = "";
  for (const value of dates) {
    if (!value) continue;
    const day = new Date(value).toISOString().slice(0, 10);
    counts.set(day, (counts.get(day) || 0) + 1);
    if (day > latest) latest = day;
  }
  if (!latest) return [];
  const series: Array<{ label: string; value: number }> = [];
  const end = new Date(`${latest}T00:00:00.000Z`);
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const day = new Date(end);
    day.setUTCDate(day.getUTCDate() - offset);
    const label = day.toISOString().slice(0, 10);
    series.push({ label, value: counts.get(label) || 0 });
  }
  return series;
}

function OverviewView({ period, onRange }: { period: Period; onRange: (range: { from: string; to: string }) => void }) {
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [error, setError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api<MetricsResponse>(withPeriod("/api/admin/metrics", period))
        .then((payload) => { if (!cancelled) { setMetrics(payload); setError(""); onRange({ from: payload.from, to: payload.to }); } })
        .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The metrics could not be loaded."); });
    };
    load();
    const poll = window.setInterval(load, 120_000);
    return () => { cancelled = true; window.clearInterval(poll); };
  }, [reloadKey, period, onRange]);

  const sync = async () => {
    setSyncing(true);
    setError("");
    try {
      await api("/api/admin/spend-sync", { method: "POST", body: JSON.stringify({ days: 90 }) });
      setReloadKey((value) => value + 1);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The spend sync failed."); }
    finally { setSyncing(false); }
  };

  if (error) return <div className="admin-alert" role="alert">{error} <button type="button" onClick={() => setReloadKey((value) => value + 1)}><RefreshCw /> Retry</button></div>;
  if (!metrics) return <div className="admin-state"><LoaderCircle className="spin" /> Loading metrics…</div>;

  const todaySpend = metrics.daily.length ? metrics.daily[metrics.daily.length - 1].spend : 0;
  const lastFullNrr = [...metrics.nrr_weeks].reverse().find((week) => week.nrr !== null && week.week_start < metrics.today);
  return <>
    <div className="admin-toolbar">
      <span className="admin-muted">{metrics.last_refreshed_at ? `Data refreshed ${new Date(metrics.last_refreshed_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}. Whop spend re-syncs every 5 minutes, this view every 2.` : "No spend data yet. Run the first sync."}</span>
      <button className="button button--secondary button--small" type="button" disabled={syncing} onClick={() => void sync()}>{syncing ? <><LoaderCircle className="spin" /> Syncing…</> : <><RefreshCw /> Sync spend (90 days)</>}</button>
    </div>
    <section className="admin-tiles">
      <article><strong>{money(todaySpend)}</strong><span>Spend today</span><Sparkline tall values={metrics.daily.slice(-14).map((day) => day.spend)} /></article>
      <article><strong>{money(metrics.month.spend)}</strong><span>Spend this month</span><Sparkline tall values={metrics.daily.filter((day) => day.date.startsWith(metrics.today.slice(0, 7))).slice(-14).map((day) => day.spend)} /></article>
      <article><strong>{money(metrics.month.profit)}</strong><span>Profit this month ({(metrics.month.rate * 100).toFixed(1)}% of spend)</span><Sparkline tall values={metrics.daily.slice(-14).map((day) => day.spend * metrics.month.rate)} /></article>
      <article><strong>{metrics.new_logos.count}</strong><span>New logos in period</span><Sparkline tall values={metrics.new_logos_daily.slice(-14).map((day) => day.count)} /></article>
      <article><strong>{metrics.churned_logos.count}</strong><span>Churned logos</span></article>
      <article><strong>{lastFullNrr?.nrr !== null && lastFullNrr !== undefined ? `${Math.round((lastFullNrr.nrr || 0) * 100)}%` : "—"}</strong><span>NRR last week</span><Sparkline tall values={metrics.nrr_weeks.map((week) => (week.nrr || 0) * 100)} /></article>
    </section>
    <section className="admin-panel">
      <h2><BarChart3 /> Daily spend, {metrics.from} to {metrics.to}</h2>
      <Bars data={metrics.daily.map((day) => ({ label: day.date, value: day.spend }))} ariaLabel="Daily spend for the selected period" format={money} unit="$" />
    </section>
    <div className="admin-detail__grid">
      <section className="admin-panel">
        <h2><TrendingUp /> Weekly NRR</h2>
        <p className="admin-muted">Spend this week from clients who spent last week, over last week's total.</p>
        <Bars data={metrics.nrr_weeks.map((week) => ({ label: `wk ${week.week_start.slice(5)}`, value: Math.round((week.nrr || 0) * 100) }))} ariaLabel="Weekly NRR" format={(value) => `${value}%`} unit="%" />
        <table className="admin-plain-table"><thead><tr><th>Week of</th><th>Prior spend</th><th>Retained</th><th>NRR</th></tr></thead><tbody>
          {metrics.nrr_weeks.map((week) => <tr key={week.week_start}><td>{week.week_start.slice(5)}</td><td>{money(week.prior_spend)}</td><td>{money(week.retained_spend)}</td><td>{week.nrr === null ? "—" : `${Math.round(week.nrr * 100)}%`}</td></tr>)}
        </tbody></table>
      </section>
      <section className="admin-panel">
        <h2><Users /> Logo movement</h2>
        <p className="admin-muted">New: first workspace created inside the selected period. Churned: spent in the prior month, nothing in 14 days.</p>
        <Bars data={metrics.new_logos_daily.map((day) => ({ label: day.date, value: day.count }))} ariaLabel="New logos per day" />
        {metrics.new_logos.owners.length > 0 && <ul className="admin-logos">
          {metrics.new_logos.owners.map((owner) => <li key={`${owner.email}${owner.created_at}`}><span className="admin-status is-active">new</span><strong>{owner.name}</strong><span className="admin-muted">{owner.email || "—"} · {formatDate(owner.created_at)}</span></li>)}
        </ul>}
        {metrics.churned_logos.owners.length > 0 && <ul className="admin-logos">
          {metrics.churned_logos.owners.map((email) => <li key={email}><span className="admin-status is-invited">churned</span><strong>{email}</strong></li>)}
        </ul>}
        {metrics.new_logos.owners.length === 0 && metrics.churned_logos.owners.length === 0 && <p className="admin-muted">No logo movement in this window.</p>}
      </section>
    </div>
    <p className="admin-footnote">Profit is {`${(metrics.month.rate * 100).toFixed(1)}%`} of spend; the rate moves to 2% in any month where platform spend reaches $10M. Last month: {money(metrics.previous_month.spend)} spend → {money(metrics.previous_month.profit)} profit.</p>
  </>;
}

function Pct({ value }: { value: number | null }) {
  if (value === null) return <span className="admin-muted">—</span>;
  return <span className={value >= 0 ? "admin-pct is-up" : "admin-pct is-down"}>{value > 0 ? "+" : ""}{value.toFixed(1)}%</span>;
}

function SpendersView({ period }: { period: Period }) {
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [budgets, setBudgets] = useState<Record<string, number | null>>({});
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    api<MetricsResponse>(withPeriod("/api/admin/metrics", period))
      .then((payload) => { if (!cancelled) setMetrics(payload); })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The spenders could not be loaded."); });
    api<{ budgets: Record<string, number | null> }>("/api/admin/spender-budgets")
      .then((payload) => { if (!cancelled) setBudgets(payload.budgets); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [period]);
  if (error) return <div className="admin-alert" role="alert">{error}</div>;
  if (!metrics) return <div className="admin-state"><LoaderCircle className="spin" /> Loading top spenders…</div>;
  return <section className="admin-panel">
    <h2><CreditCard /> Top spenders</h2>
    <Bars data={metrics.daily.map((day, index) => ({
      label: day.date,
      value: metrics.top_spenders.reduce((sum, entry) => sum + (entry.daily[index] || 0), 0),
    }))} ariaLabel="Daily spend across ranked clients" format={money} unit="$" />
    <div className="admin-table-scroll">
    <table className="admin-plain-table admin-plain-table--wide"><thead><tr><th>First $1k</th><th>Company</th><th>Account</th><th>Daily budget</th><th>Today</th><th>DoD %</th><th>L3</th><th>L3 %</th><th>L7</th><th>WoW %</th><th>L30</th><th>L30 %</th><th>Range</th><th>Trend</th><th>Owner</th></tr></thead><tbody>
      {metrics.top_spenders.map((entry) => <tr key={entry.whop_account_id}>
        <td>{entry.first_1k_spend_date ? formatDate(entry.first_1k_spend_date) : "—"}</td>
        <td><strong>{entry.name}</strong></td>
        <td className="admin-mono">{entry.whop_account_id}</td>
        <td>{budgets[entry.whop_account_id] != null ? money(budgets[entry.whop_account_id] as number) : "—"}</td>
        <td>{money(entry.today_spend)}</td>
        <td><Pct value={entry.dod_change_pct} /></td>
        <td>{money(entry.l3_spend)}</td>
        <td><Pct value={entry.l3_change_pct} /></td>
        <td>{money(entry.l7_spend)}</td>
        <td><Pct value={entry.wow_change_pct} /></td>
        <td>{money(entry.l30_spend)}</td>
        <td><Pct value={entry.l30_change_pct} /></td>
        <td>{money(entry.range_spend)}</td>
        <td><Sparkline values={entry.daily} /></td>
        <td>{entry.owner_email || "—"}</td>
      </tr>)}
    </tbody></table>
    </div>
    {metrics.top_spenders.length === 0 && <p className="admin-muted">No spend recorded yet. Run a spend sync from Overview.</p>}
  </section>;
}

function FailuresView({ period }: { period: Period }) {
  const [data, setData] = useState<FailuresResponse | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api<FailuresResponse>(withPeriod("/api/admin/payment-failures", period))
      .then((payload) => { if (!cancelled) { setData(payload); setError(""); } })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The payment failures could not be loaded."); });
    return () => { cancelled = true; };
  }, [reloadKey, period]);

  const retry = async (campaignId: string, accountId: string) => {
    setBusy(campaignId);
    setError("");
    setNotice("");
    try {
      await api(`/api/admin/campaigns/${encodeURIComponent(campaignId)}/retry-payment`, { method: "POST", body: JSON.stringify({ account_id: accountId }) });
      setNotice("Payment retry requested. The charge re-attempts shortly.");
      setReloadKey((value) => value + 1);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The payment retry failed."); }
    finally { setBusy(""); }
  };

  if (error && !data) return <div className="admin-alert" role="alert">{error}</div>;
  if (!data) return <div className="admin-state"><LoaderCircle className="spin" /> Scanning campaigns for payment failures…</div>;

  return <>
    {error && <div className="admin-alert" role="alert">{error}</div>}
    {notice && <div className="admin-notice" role="status">{notice}</div>}
    <section className="admin-panel">
      <h2><AlertTriangle /> Campaigns with failed spend charges</h2>
      <p className="admin-muted">Admins are emailed when a campaign's spend charge fails. Retry the charge here once the client updates their card.</p>
      {data.failures.length > 0 && <Bars data={dailyCounts(data.failures.map((failure) => failure.updated_at), 30)} ariaLabel="Failed campaigns by last update day" />}
      {data.failures.length === 0 && <p className="admin-ok"><CheckCircle2 /> No campaigns are in payment-failed state right now.</p>}
      {data.failures.length > 0 && <table className="admin-plain-table"><thead><tr><th>Campaign</th><th>Client</th><th>Owner</th><th>Last update</th><th /></tr></thead><tbody>
        {data.failures.map((failure) => <tr key={failure.campaign_id}>
          <td><strong>{failure.title}</strong><br /><span className="admin-mono">{failure.campaign_id}</span></td>
          <td>{failure.account_name}</td>
          <td>{failure.owner_email || "—"}</td>
          <td>{formatDate(failure.updated_at)}</td>
          <td><button className="button button--secondary button--small" type="button" disabled={busy === failure.campaign_id} onClick={() => void retry(failure.campaign_id, failure.account_id)}>{busy === failure.campaign_id ? "Retrying…" : "Retry charge"}</button></td>
        </tr>)}
      </tbody></table>}
    </section>
    <section className="admin-panel">
      <h2><CreditCard /> Payment failure history</h2>
      <p className="admin-muted">Every recorded spend-charge failure in the selected period, including recovered ones.</p>
      {data.history.length > 0 && <Bars data={dailyCounts(data.history.map((entry) => entry.first_seen_at), 30)} ariaLabel="Payment failures per day" />}
      {data.history.length === 0 && <p className="admin-muted">No payment failures recorded in this period.</p>}
      {data.history.length > 0 && <table className="admin-plain-table"><thead><tr><th>First seen</th><th>Status</th><th>Client</th><th>Owner</th><th>Campaign</th></tr></thead><tbody>
        {data.history.map((entry) => <tr key={entry.id}>
          <td>{formatDate(entry.first_seen_at)}</td>
          <td>{entry.resolved_at ? <span className="admin-status is-active">recovered {formatDate(entry.resolved_at)}</span> : <span className="admin-status is-invited">ongoing</span>}</td>
          <td>{entry.account_name}</td>
          <td>{entry.owner_email || "—"}</td>
          <td className="admin-mono">{entry.campaign_id}</td>
        </tr>)}
      </tbody></table>}
    </section>
    <section className="admin-panel">
      <h2><XCircle /> Campaign launch rejections</h2>
      <p className="admin-muted">Every launch the ad network declined, with the exact reason. Kept permanently.</p>
      {data.launch_failures.length > 0 && <Bars data={dailyCounts(data.launch_failures.map((failure) => failure.created_at), 30)} ariaLabel="Launch rejections per day" />}
      {data.launch_failures.length === 0 && <p className="admin-muted">No launch rejections recorded yet.</p>}
      {data.launch_failures.length > 0 && <table className="admin-plain-table"><thead><tr><th>When</th><th>Client</th><th>Owner</th><th>Campaign</th><th>Reason</th></tr></thead><tbody>
        {data.launch_failures.map((failure) => <tr key={failure.id}>
          <td>{formatDate(failure.created_at)} {new Date(failure.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</td>
          <td>{failure.account_name}</td>
          <td>{failure.owner_email || "—"}</td>
          <td className="admin-mono">{failure.campaign_id}</td>
          <td>{failure.reason || "—"}</td>
        </tr>)}
      </tbody></table>}
    </section>
    <section className="admin-panel">
      <h2><Mail /> Payment webhooks</h2>
      <p className="admin-muted">Payment events delivered to this platform's webhook endpoint.</p>
      {data.webhook_events.length > 0 && <Bars data={dailyCounts(data.webhook_events.map((event) => event.received_at), 30)} ariaLabel="Payment webhook deliveries per day" />}
      {data.webhook_events.length === 0 && <p className="admin-muted">No payment webhook deliveries received yet.</p>}
      {data.webhook_events.length > 0 && <table className="admin-plain-table"><thead><tr><th>Type</th><th>Company</th><th>Verified</th><th>Received</th></tr></thead><tbody>
        {data.webhook_events.map((event) => <tr key={event.event_id}><td>{event.type}</td><td className="admin-mono">{event.company_id || "—"}</td><td>{event.verified ? "yes" : "no"}</td><td>{formatDate(event.received_at)}</td></tr>)}
      </tbody></table>}
    </section>
  </>;
}

type UserSortKey = "email" | "accounts" | "created" | "seen";

function UsersView() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState("");
  const [sort, setSort] = useState<{ key: UserSortKey; dir: 1 | -1 }>({ key: "created", dir: -1 });
  const toggleSort = (key: UserSortKey) => setSort((current) => current.key === key
    ? { key, dir: current.dir === 1 ? -1 : 1 }
    : { key, dir: key === "email" ? 1 : -1 });

  useEffect(() => {
    let cancelled = false;
    api<{ users: AdminUser[] }>("/api/admin/users")
      .then((payload) => { if (!cancelled) { setUsers(payload.users); setError(""); } })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The users could not be loaded."); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const viewAsUser = async (user: AdminUser) => {
    setBusy(`view:${user.id}`);
    setError("");
    try {
      const grant = await api<{ token: string }>("/api/admin/impersonate-user", { method: "POST", body: JSON.stringify({ user_id: user.id }) });
      window.open(`/app?imp=${encodeURIComponent(grant.token)}`, "_blank", "noopener");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The user session could not be opened."); }
    finally { setBusy(""); }
  };

  const filtered = useMemo(() => {
    if (!users) return [];
    const needle = query.trim().toLowerCase();
    const matched = needle
      ? users.filter((user) => user.email.includes(needle) || user.account_names.some((name) => name.toLowerCase().includes(needle)))
      : users;
    const rank = (user: AdminUser): string | number => {
      if (sort.key === "email") return user.email;
      if (sort.key === "accounts") return user.account_count;
      if (sort.key === "created") return user.created_at || "";
      return user.last_sign_in_at || "";
    };
    return [...matched].sort((a, b) => {
      const left = rank(a);
      const right = rank(b);
      const order = typeof left === "number" && typeof right === "number" ? left - right : String(left).localeCompare(String(right));
      return order * sort.dir;
    });
  }, [users, query, sort]);

  // Matching account names float to the front of the preview so a search hit
  // is visible even when the user has more accounts than the row can show.
  const namesPreview = (user: AdminUser) => {
    const needle = query.trim().toLowerCase();
    const names = needle
      ? [...user.account_names].sort((a, b) => Number(b.toLowerCase().includes(needle)) - Number(a.toLowerCase().includes(needle)))
      : user.account_names;
    return `${names.slice(0, 3).join(", ")}${names.length > 3 ? ` +${names.length - 3} more` : ""}`;
  };

  const sortHeader = (label: string, key: UserSortKey) => (
    <th aria-sort={sort.key === key ? (sort.dir === 1 ? "ascending" : "descending") : undefined}>
      <button type="button" className={sort.key === key ? "is-sorted" : ""} onClick={() => toggleSort(key)}>
        {label}<span aria-hidden="true">{sort.key === key ? (sort.dir === 1 ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );

  if (error && !users) return <div className="admin-alert" role="alert">{error} <button type="button" onClick={() => setReloadKey((value) => value + 1)}><RefreshCw /> Retry</button></div>;
  if (!users) return <div className="admin-state"><LoaderCircle className="spin" /> Loading users…</div>;

  return <>
    {error && <div className="admin-alert" role="alert">{error}</div>}
    <section className="admin-tiles">
      <article><strong>{users.length}</strong><span>Users</span></article>
      <article><strong>{users.reduce((sum, user) => sum + user.account_count, 0)}</strong><span>Ad accounts</span></article>
    </section>
    <div className="admin-search"><Search /><input value={query} placeholder="Search by email or ad account name" onChange={(event) => setQuery(event.target.value)} /></div>
    <section className="admin-panel">
      <h2><Users /> Users</h2>
      <p className="admin-muted">Every verified user on the platform. "View as user" opens exactly what they see.</p>
      <div className="admin-table-scroll">
      <table className="admin-plain-table admin-plain-table--wide"><thead><tr>{sortHeader("Email", "email")}{sortHeader("Ad accounts", "accounts")}{sortHeader("Signed up", "created")}{sortHeader("Last sign-in", "seen")}<th /></tr></thead><tbody>
        {filtered.map((user) => <tr key={user.id}>
          <td><strong>{user.email || "—"}</strong></td>
          <td title={user.account_names.join(", ")}>{user.account_count > 0 ? <>{user.account_count} <span className="admin-muted">· {namesPreview(user)}</span></> : <span className="admin-muted">none yet</span>}</td>
          <td>{formatDate(user.created_at)}</td>
          <td>{formatDate(user.last_sign_in_at)}</td>
          <td><button className="button button--primary button--small" type="button" disabled={busy === `view:${user.id}` || user.account_count === 0} onClick={() => void viewAsUser(user)}>{busy === `view:${user.id}` ? "Opening…" : "View as user"}</button></td>
        </tr>)}
      </tbody></table>
      </div>
      {filtered.length === 0 && <p className="admin-muted">No users match that search.</p>}
    </section>
  </>;
}

type WorkspaceSortKey = "name" | "owner" | "members" | "created";

function WorkspacesView() {
  const [data, setData] = useState<AdminWorkspacesResponse | null>(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{ key: WorkspaceSortKey; dir: 1 | -1 }>({ key: "created", dir: -1 });
  const toggleSort = (key: WorkspaceSortKey) => setSort((current) => current.key === key
    ? { key, dir: current.dir === 1 ? -1 : 1 }
    : { key, dir: key === "name" || key === "owner" ? 1 : -1 });
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminWorkspaceDetail | null>(null);
  const [detailError, setDetailError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api<AdminWorkspacesResponse>("/api/admin/workspaces")
      .then((payload) => { if (!cancelled) { setData(payload); setError(""); } })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The ad accounts could not be loaded."); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const openWorkspace = (id: string | null) => {
    setSelected(id);
    setDetail(null);
    setDetailError("");
  };

  const [manageBusy, setManageBusy] = useState("");
  const manage = async (id: string) => {
    setManageBusy(id);
    try {
      const grant = await api<{ token: string }>("/api/admin/impersonate", { method: "POST", body: JSON.stringify({ workspace_id: id }) });
      window.open(`/app?imp=${encodeURIComponent(grant.token)}`, "_blank", "noopener");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The managed session could not be opened."); }
    finally { setManageBusy(""); }
  };

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    api<AdminWorkspaceDetail>(`/api/admin/workspaces/${encodeURIComponent(selected)}`)
      .then((payload) => { if (!cancelled) setDetail(payload); })
      .catch((caught) => { if (!cancelled) setDetailError(caught instanceof Error ? caught.message : "The ad account could not be loaded."); });
    return () => { cancelled = true; };
  }, [selected]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = query.trim().toLowerCase();
    const matched = needle
      ? data.workspaces.filter((workspace) =>
          workspace.name.toLowerCase().includes(needle)
          || workspace.owner_email.includes(needle)
          || workspace.whop_account_id.toLowerCase().includes(needle))
      : data.workspaces;
    const rank = (workspace: AdminWorkspace): string | number => {
      if (sort.key === "name") return workspace.name.toLowerCase();
      if (sort.key === "owner") return workspace.owner_email;
      if (sort.key === "members") return workspace.members;
      return workspace.created_at || "";
    };
    return [...matched].sort((a, b) => {
      const left = rank(a);
      const right = rank(b);
      const order = typeof left === "number" && typeof right === "number" ? left - right : String(left).localeCompare(String(right));
      return order * sort.dir;
    });
  }, [data, query, sort]);

  const sortHeader = (label: string, key: WorkspaceSortKey) => (
    <button type="button" className={`admin-sort ${sort.key === key ? "is-sorted" : ""}`} onClick={() => toggleSort(key)}>
      {label}<span aria-hidden="true">{sort.key === key ? (sort.dir === 1 ? "▲" : "▼") : "↕"}</span>
    </button>
  );

  if (error) return <div className="admin-alert" role="alert">{error} <button type="button" onClick={() => setReloadKey((value) => value + 1)}><RefreshCw /> Retry</button></div>;
  if (!data) return <div className="admin-state"><LoaderCircle className="spin" /> Loading ad accounts…</div>;

  return <>
    <section className="admin-tiles">
      <article><strong>{data.totals.workspaces}</strong><span>Ad accounts</span></article>
      <article><strong>{data.totals.clients}</strong><span>Clients</span></article>
      <article><strong>{data.totals.teammates}</strong><span>Teammates</span></article>
      <article><strong>{data.totals.pending_invites}</strong><span>Pending invites</span></article>
      <article><strong>{data.totals.new_this_week}</strong><span>New this week</span></article>
    </section>

    {!selected && <>
      <section className="admin-panel"><h2><Building2 /> New ad accounts per day</h2><Bars data={dailyCounts(data.workspaces.map((workspace) => workspace.created_at), 30)} ariaLabel="New ad accounts per day" /></section>
      <div className="admin-search"><Search /><input value={query} placeholder="Search by business, owner email, or account id" onChange={(event) => setQuery(event.target.value)} /></div>
      <section className="admin-table" role="table" aria-label="Client ad accounts">
        <header role="row"><span>{sortHeader("Business", "name")}</span><span>{sortHeader("Owner", "owner")}</span><span>{sortHeader("Members", "members")}</span><span>Account</span><span>{sortHeader("Created", "created")}</span></header>
        {filtered.map((workspace) => (
          <button type="button" key={workspace.id} role="row" onClick={() => openWorkspace(workspace.id)}>
            <span><Building2 />{workspace.name}</span>
            <span>{workspace.owner_email || "—"}</span>
            <span>{workspace.members}</span>
            <span className="admin-mono">{workspace.whop_account_id}</span>
            <span>{formatDate(workspace.created_at)}</span>
          </button>
        ))}
        {filtered.length === 0 && <div className="admin-empty">No ad accounts match that search.</div>}
      </section>
    </>}

    {selected && <section className="admin-detail">
      <button className="admin-back" type="button" onClick={() => openWorkspace(null)}><ArrowLeft /> All ad accounts</button>
      {detailError && <div className="admin-alert" role="alert">{detailError}</div>}
      {!detail && !detailError && <div className="admin-state"><LoaderCircle className="spin" /> Loading ad account…</div>}
      {detail && <>
        <div className="admin-detail__head">
          <div><strong>{detail.workspace.name}</strong><span><Mail /> {detail.workspace.owner_email || "owner unknown"}</span></div>
          <div className="admin-detail__head-actions">
            <span className="admin-mono">{detail.workspace.whop_account_id}</span>
            <button className="button button--primary button--small" type="button" disabled={manageBusy === detail.workspace.id} onClick={() => void manage(detail.workspace.id)}>{manageBusy === detail.workspace.id ? "Opening…" : "Manage ad account"}</button>
          </div>
        </div>
        <div className="admin-detail__grid">
          <article className="admin-panel">
            <h2>Setup</h2>
            {detail.setup ? <ul className="admin-checks">
              <li>{detail.setup.billingReady ? <CheckCircle2 className="is-ok" /> : <XCircle className="is-bad" />} Billing card connected</li>
              <li>{detail.setup.identityReady ? <CheckCircle2 className="is-ok" /> : <XCircle className="is-bad" />} Meta identity ready</li>
            </ul> : <p className="admin-muted">Live setup state is unavailable right now.</p>}
            <h2>Campaigns</h2>
            {detail.campaigns ? <p>{detail.campaigns.active} active of {detail.campaigns.total} recent campaigns</p> : <p className="admin-muted">Campaign data is unavailable right now.</p>}
            <h2>Created</h2>
            <p>{formatDate(detail.workspace.created_at)}</p>
          </article>
          <article className="admin-panel">
            <h2><Users /> Team ({detail.members.length + 1})</h2>
            <ul className="admin-members">
              <li><strong>{detail.workspace.owner_email || "Owner"}</strong><span className="admin-role">owner</span><span className="admin-status is-active">active</span></li>
              {detail.members.map((member) => (
                <li key={member.id}><strong>{member.email}</strong><span className="admin-role">{member.role}</span><span className={`admin-status ${member.status === "active" ? "is-active" : "is-invited"}`}>{member.status}</span></li>
              ))}
            </ul>
          </article>
        </div>
      </>}
    </section>}
  </>;
}

type PlatformAdminEntry = { id: string | null; email: string; source: "config" | "added"; added_by: string | null; created_at: string | null };

function AdminsView() {
  const [admins, setAdmins] = useState<PlatformAdminEntry[] | null>(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api<{ admins: PlatformAdminEntry[] }>("/api/admin/admins")
      .then((payload) => { if (!cancelled) setAdmins(payload.admins); })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The administrators could not be loaded."); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const add = async () => {
    const value = email.trim().toLowerCase();
    if (!value.includes("@")) { setError("Enter a valid email address."); return; }
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api("/api/admin/admins", { method: "POST", body: JSON.stringify({ email: value }) });
      setEmail("");
      setNotice(`${value} can now open the admin dashboard on this platform.`);
      setReloadKey((current) => current + 1);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The administrator could not be added."); }
    finally { setBusy(false); }
  };

  const remove = async (entry: PlatformAdminEntry) => {
    if (!entry.id) return;
    if (!window.confirm(`Remove ${entry.email} from the platform administrators?`)) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await api(`/api/admin/admins/${encodeURIComponent(entry.id)}`, { method: "DELETE" });
      setReloadKey((current) => current + 1);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "The administrator could not be removed."); }
    finally { setBusy(false); }
  };

  return <section className="team-workspace">
    <div className="team-invite">
      <div><strong>Administrators</strong><span>Admins can open the admin dashboard and manage this list. Built-in admins come from the platform configuration.</span></div>
      <div className="team-invite__form">
        <input value={email} type="email" placeholder="teammate@company.com" onChange={(event) => setEmail(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void add(); }} />
        <button className="button button--primary button--small" type="button" disabled={busy} onClick={() => void add()}>{busy ? "Saving…" : "Add admin"}</button>
      </div>
    </div>
    {error && <p className="team-error" role="alert">{error}</p>}
    {notice && <p className="team-notice" role="status">{notice}</p>}
    {!admins && !error && <div className="team-loading">Loading administrators…</div>}
    {admins && <div className="team-list">
      {admins.map((entry) => <article key={entry.id || entry.email}>
        <div className="team-list__top">
          <div className="team-list__who"><strong>{entry.email}</strong><span>{entry.source === "config" ? "Built-in, set in the platform configuration" : `Added${entry.added_by ? ` by ${entry.added_by}` : ""}`}</span></div>
          {entry.source === "added"
            ? <button className="team-list__remove" type="button" disabled={busy} onClick={() => void remove(entry)} aria-label={`Remove ${entry.email}`}><Trash2 /></button>
            : <span className="admin-builtin"><ShieldCheck /> Built-in</span>}
        </div>
      </article>)}
    </div>}
  </section>;
}

type WebhookEventsResponse = {
  types: string[];
  events: Array<{ event_id: string; type: string; company_id: string | null; verified: boolean; received_at: string; payload: unknown }>;
};

function EventsView({ period }: { period: Period }) {
  const [data, setData] = useState<WebhookEventsResponse | null>(null);
  const [error, setError] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api<WebhookEventsResponse>(withPeriod("/api/admin/webhook-events", period, typeFilter ? `type=${encodeURIComponent(typeFilter)}` : ""))
      .then((payload) => { if (!cancelled) { setData(payload); setError(""); } })
      .catch((caught) => { if (!cancelled) setError(caught instanceof Error ? caught.message : "The events could not be loaded."); });
    return () => { cancelled = true; };
  }, [typeFilter, reloadKey, period]);

  if (error) return <div className="admin-alert" role="alert">{error} <button type="button" onClick={() => setReloadKey((value) => value + 1)}><RefreshCw /> Retry</button></div>;
  if (!data) return <div className="admin-state"><LoaderCircle className="spin" /> Loading events…</div>;

  return <section className="admin-panel">
    <div className="admin-toolbar">
      <h2 style={{ margin: 0 }}><Webhook /> Whop webhook events</h2>
      <div className="admin-toolbar__controls">
        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} aria-label="Filter by event type">
          <option value="">All types</option>
          {data.types.map((type) => <option key={type} value={type}>{type}</option>)}
        </select>
        <button className="button button--secondary button--small" type="button" onClick={() => setReloadKey((value) => value + 1)}><RefreshCw /> Refresh</button>
      </div>
    </div>
    <p className="admin-muted">Every delivery to /api/webhooks/whop, newest first. Unverified means no webhook secret is configured yet or the signature did not match.</p>
    {data.events.length > 0 && <Bars data={dailyCounts(data.events.map((event) => event.received_at), 30)} ariaLabel="Webhook deliveries per day" />}
    {data.events.length === 0 && <p className="admin-muted">No deliveries stored yet.</p>}
    {data.events.map((event) => <details className="admin-event" key={event.event_id}>
      <summary>
        <span className="admin-event__type">{event.type}</span>
        <span className="admin-mono">{event.company_id || "—"}</span>
        <span className={`admin-status ${event.verified ? "is-active" : "is-invited"}`}>{event.verified ? "verified" : "unverified"}</span>
        <span className="admin-muted">{formatDate(event.received_at)} {new Date(event.received_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
      </summary>
      <pre>{JSON.stringify(event.payload, null, 2)}</pre>
    </details>)}
  </section>;
}

export function AdminConsole() {
  const [tab, setTab] = useState<AdminTab>("overview");
  const [forbidden, setForbidden] = useState(false);
  const [checked, setChecked] = useState(false);
  const [period, setPeriod] = useState<Period>(null);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    api("/api/admin/workspaces")
      .then(() => { if (!cancelled) { setForbidden(false); setChecked(true); } })
      .catch((caught) => {
        if (cancelled) return;
        const message = caught instanceof Error ? caught.message : "";
        setForbidden(message.includes("platform administrators"));
        setChecked(true);
      });
    return () => { cancelled = true; };
  }, []);

  if (checked && forbidden) {
    return <div className="admin-gate admin-gate--inline">
      <ShieldAlert />
      <h1>Administrators only</h1>
      <p>This account does not have platform admin access.</p>
    </div>;
  }

  return <div className="admin-console">
    <nav className="admin-tabs" role="tablist" aria-label="Admin views">
      <button role="tab" type="button" aria-selected={tab === "overview"} className={tab === "overview" ? "is-active" : ""} onClick={() => setTab("overview")}><BarChart3 />Overview</button>
      <button role="tab" type="button" aria-selected={tab === "ledger"} className={tab === "ledger" ? "is-active" : ""} onClick={() => setTab("ledger")}><Wallet />Ledger</button>
      <button role="tab" type="button" aria-selected={tab === "spenders"} className={tab === "spenders" ? "is-active" : ""} onClick={() => setTab("spenders")}><CreditCard />Top spenders</button>
      <button role="tab" type="button" aria-selected={tab === "failures"} className={tab === "failures" ? "is-active" : ""} onClick={() => setTab("failures")}><AlertTriangle />Payment failures</button>
      <button role="tab" type="button" aria-selected={tab === "users"} className={tab === "users" ? "is-active" : ""} onClick={() => setTab("users")}><Users />Users</button>
      <button role="tab" type="button" aria-selected={tab === "workspaces"} className={tab === "workspaces" ? "is-active" : ""} onClick={() => setTab("workspaces")}><Building2 />Ad accounts</button>
      <button role="tab" type="button" aria-selected={tab === "events"} className={tab === "events" ? "is-active" : ""} onClick={() => setTab("events")}><Webhook />Events</button>
      <button role="tab" type="button" aria-selected={tab === "admins"} className={tab === "admins" ? "is-active" : ""} onClick={() => setTab("admins")}><ShieldCheck />Administrators</button>
    </nav>
    {tab !== "admins" && tab !== "workspaces" && tab !== "users" && tab !== "ledger" && <PeriodBar period={period} onChange={setPeriod} range={range} />}
    {tab === "overview" && <OverviewView period={period} onRange={setRange} />}
    {tab === "ledger" && <LedgerView />}
    {tab === "spenders" && <SpendersView period={period} />}
    {tab === "failures" && <FailuresView period={period} />}
    {tab === "users" && <UsersView />}
    {tab === "workspaces" && <WorkspacesView />}
    {tab === "events" && <EventsView period={period} />}
    {tab === "admins" && <AdminsView />}
  </div>;
}

type LedgerAccount = { id: string; name: string; whop_account_id: string; credit_cents: number; spend_cents: number; remaining_cents: number };
type LedgerResponse = {
  master: { ledger_id: string | null; balances: Array<Record<string, unknown>>; approval_status: string | null } | null;
  accounts: LedgerAccount[];
  pauses: Array<{ ad_account_id: string; campaign_id: string; paused_at: string }>;
};

const ledgerCents = (value: number) => `$${(value / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Double-ledger console: Higgsfield deposits fund the parent master balance on
// Whop; each sub-account gets a credit here (paid to Higgsfield directly) that
// its ad spend draws down. The scheduler pauses campaigns at zero.
function LedgerView() {
  const [data, setData] = useState<LedgerResponse | null | undefined>(undefined);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [epoch, setEpoch] = useState(0);
  useEffect(() => {
    let cancelled = false;
    api<LedgerResponse>("/api/admin/ledger").then((payload) => { if (!cancelled) setData(payload); }).catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [epoch]);
  const topUp = async (account: LedgerAccount) => {
    const dollars = Number(amounts[account.id]);
    if (!Number.isFinite(dollars) || dollars === 0) { setError("Enter a non-zero dollar amount."); return; }
    setBusyId(account.id);
    setError("");
    try {
      await api("/api/admin/ledger/top-up", { method: "POST", body: JSON.stringify({ ad_account_id: account.id, amount_cents: Math.round(dollars * 100), note: "Manual top-up from the admin console" }) });
      setAmounts((current) => ({ ...current, [account.id]: "" }));
      setEpoch((value) => value + 1);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The top-up could not be recorded.");
    } finally {
      setBusyId("");
    }
  };
  if (data === undefined) return <div className="admin-loading"><LoaderCircle className="spin" /> Loading the ledger…</div>;
  if (data === null) return <div className="admin-loading">The ledger could not be loaded. <button className="button button--secondary button--small" type="button" onClick={() => setEpoch((value) => value + 1)}>Try again</button></div>;
  const masterBalance = (data.master?.balances || []).map((balance) => {
    const amount = Number(balance.available ?? balance.amount ?? balance.balance ?? 0);
    const currency = String(balance.currency || "usd").toUpperCase();
    return `${ledgerCents(Math.round(amount * 100))} ${currency}`;
  }).join(" · ");
  const allocated = data.accounts.reduce((total, account) => total + account.credit_cents, 0);
  const spent = data.accounts.reduce((total, account) => total + account.spend_cents, 0);
  return <div className="ledger-view">
    {error && <div className="form-alert" role="alert">{error}</div>}
    <div className="ledger-master">
      <article><span>Master balance (Whop)</span><strong>{masterBalance || "$0.00"}</strong><small>{data.master?.approval_status === "pending" ? "KYC pending: deposits can land, ads billing unlocks once verified" : `ledger ${data.master?.ledger_id || "unknown"}`}</small></article>
      <article><span>Allocated to customers</span><strong>{ledgerCents(allocated)}</strong><small>sum of every customer's ad balance</small></article>
      <article><span>Spent on ads</span><strong>{ledgerCents(spent)}</strong><small>drawn down from the master balance</small></article>
      <article><span>Unspent allocation</span><strong>{ledgerCents(allocated - spent)}</strong><small>committed but not yet delivered</small></article>
    </div>
    <div className="ledger-table">
      <div className="ledger-table__head"><span>Customer</span><span>Ad balance</span><span>Spent</span><span>Remaining</span><span>Top up</span></div>
      {data.accounts.map((account) => <div className="ledger-table__row" key={account.id}>
        <span className="ledger-table__name">{account.name}<small>{account.whop_account_id}</small></span>
        <span>{ledgerCents(account.credit_cents)}</span>
        <span>{ledgerCents(account.spend_cents)}</span>
        <span className={account.credit_cents > 0 && account.remaining_cents <= 0 ? "is-exhausted" : account.credit_cents > 0 ? "is-funded" : ""}>{account.credit_cents > 0 ? ledgerCents(account.remaining_cents) : "no credit yet"}</span>
        <span className="ledger-table__topup"><input inputMode="decimal" placeholder="$" value={amounts[account.id] || ""} onChange={(event) => setAmounts((current) => ({ ...current, [account.id]: event.target.value }))} /><button className="button button--primary button--small" type="button" disabled={busyId === account.id} onClick={() => void topUp(account)}>{busyId === account.id ? "Saving…" : "Add"}</button></span>
      </div>)}
      {data.accounts.length === 0 && <div className="ledger-table__row"><span>No customer accounts yet.</span></div>}
    </div>
    {data.pauses.length > 0 && <div className="ledger-pauses"><strong>Auto-paused campaigns</strong>{data.pauses.slice(0, 8).map((pause) => <span key={`${pause.campaign_id}-${pause.paused_at}`}>{pause.campaign_id} · {pause.paused_at.slice(0, 16).replace("T", " ")}</span>)}</div>}
  </div>;
}
